import { Link } from 'react-router-dom';
import type { BlogPost } from '../../../mocks/blog';
import { getAuthorByName } from '../../../mocks/authors';

interface ArticleHeroProps {
  post: BlogPost;
}

export default function ArticleHero({ post }: ArticleHeroProps) {
  return (
    <section className="relative min-h-[70vh] flex items-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16 pt-40">
        <div className="max-w-3xl">
          {/* Category + tags row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className={`text-xs font-semibold rounded-full px-3.5 py-1.5 border ${post.categoryColor} border-current/20`}>
              {post.category}
            </span>
            {post.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-zinc-400 bg-zinc-800/70 border border-zinc-700/50 rounded-full px-3 py-1">
                #{tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.05] tracking-tight mb-8">
            {post.title}
          </h1>

          {/* Meta bar */}
          <div className="flex flex-wrap items-center gap-6">
            <Link
              to={`/autor/${getAuthorByName(post.author)?.id ?? ''}`}
              className="flex items-center gap-3 group cursor-pointer"
            >
              <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-orange-500/40 group-hover:border-orange-400 transition-colors duration-200">
                <img
                  src={post.authorImage}
                  alt={post.author}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-white text-sm font-bold group-hover:text-orange-300 transition-colors duration-200">{post.author}</p>
                <p className="text-zinc-400 text-xs">{post.authorRole}</p>
              </div>
            </Link>

            <div className="flex items-center gap-1 text-zinc-500">
              <div className="w-px h-5 bg-zinc-700" />
            </div>

            <div className="flex items-center gap-1.5 text-zinc-400 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-orange-400" />
              </div>
              <span>{post.date}</span>
            </div>

            <div className="flex items-center gap-1.5 text-zinc-400 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-orange-400" />
              </div>
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
